import { CreditCard, RefreshCw } from "lucide-react";
import {
  useAdminBilling,
  useSyncSubscriptions,
} from "@/features/payments/hooks/usePayments";
import { dollars } from "@/features/payments/payments.types";
import { StatGridSkeleton, TableSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card className="animate-item-enter">
      <CardHeader>
        <p className="text-3xl font-semibold tabular-nums">{value}</p>
        <CardDescription>{label}</CardDescription>
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      </CardHeader>
    </Card>
  );
}

export default function BillingPage() {
  const { data, isPending, error } = useAdminBilling();
  const sync = useSyncSubscriptions();

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl">Billing</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        Money only. <strong>Gifted members are not counted here</strong>: they
        have full access but were never charged, so including them would
        overstate revenue.
      </p>

      {error && <p className="text-destructive mt-6 text-sm">{error.message}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isPending || !data ? (
          <StatGridSkeleton />
        ) : (
          <>
            <Stat
              label="All-time revenue"
              value={dollars(data.totals.allTimeRevenueCents)}
            />
            <Stat label="Payments" value={String(data.totals.paymentCount)} />
            <Stat
              label="Paying members"
              value={String(data.totals.activePaidMembers)}
              hint="Excludes gifts"
            />
            <Stat
              label="Estimated annual"
              value={dollars(data.totals.estimatedAnnualCents)}
            />
          </>
        )}
      </div>

      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={sectionTitleClass}>Subscriptions</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            {sync.isPending ? (
              <Spinner data-slot="icon" />
            ) : (
              <RefreshCw data-slot="icon" />
            )}
            {sync.isPending ? "Syncing…" : "Sync from Stripe"}
          </Button>
        </div>
        {sync.data && (
          <p className="mt-2 text-sm text-green-600">{sync.data.message}</p>
        )}
        {sync.error && (
          <p className="text-destructive mt-2 text-sm">{sync.error.message}</p>
        )}

        <div className="mt-3">
          {isPending || !data ? (
            <TableSkeleton rows={4} columns={4} />
          ) : data.subscribers.length === 0 ? (
            <Card className="animate-page-enter">
              <CardContent className="flex items-start gap-3 py-10">
                <CreditCard
                  className="text-muted-foreground mt-0.5 size-5"
                  strokeWidth={1.5}
                />
                <div className="text-sm">
                  <p>No Stripe subscriptions yet.</p>
                  <p className="text-muted-foreground mt-1">
                    Members imported from Squarespace have no subscription in
                    Stripe, so this stays empty until billing moves across.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="ring-foreground/10 overflow-x-auto rounded-xl ring-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Renews</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.subscribers.map((sub) => (
                    <TableRow key={sub.member_id}>
                      <TableCell>
                        <p className="font-medium">{sub.name}</p>
                        <p className="text-muted-foreground text-xs">
                          {sub.email}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {sub.subscription_status || "none"}
                        </Badge>
                        {sub.cancel_at_period_end && (
                          <Badge variant="outline" className="ml-1">
                            cancels
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {sub.amount_cents != null
                          ? `${dollars(sub.amount_cents)}/yr`
                          : "none"}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {sub.current_period_end
                          ? new Date(sub.current_period_end).toLocaleDateString()
                          : "none"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </section>

      <section className="mt-8 pb-8">
        <h2 className={sectionTitleClass}>Payments ledger</h2>
        <div className="mt-3">
          {isPending || !data ? (
            <TableSkeleton rows={6} columns={4} />
          ) : data.ledger.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              No payments yet.
            </p>
          ) : (
            <div className="ring-foreground/10 overflow-x-auto rounded-xl ring-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.ledger.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.member_name}
                      </TableCell>
                      <TableCell>
                        {row.kind}
                        {row.simulated && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            simulated
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {dollars(row.amount_cents)}
                      </TableCell>
                      <TableCell
                        className="text-muted-foreground text-xs"
                        title={row.stripe_ref ?? undefined}
                      >
                        {new Date(row.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
